import React, { useState, useMemo } from 'react';
import { useWallets } from '../WalletContext';
import api from '../services/api';
import './Reports.css';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

const CURRENCY_SYMBOLS = { EUR: '€', GBP: '£', USD: '$', CNY: '¥', JPY: '¥', CHF: 'CHF' };

function Reports() {
  const { wallets } = useWallets();

  // Date range
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Options
  const [includeIncome, setIncludeIncome] = useState(true);
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [includeHousehold, setIncludeHousehold] = useState(true);
  const [selectedWallets, setSelectedWallets] = useState(['all']);
  const [language, setLanguage] = useState('en');
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Frontend currency validation
  const currencyValidation = useMemo(() => {
    const walletsToCheck = selectedWallets.includes('all') 
      ? wallets 
      : wallets.filter(w => selectedWallets.includes(w.id));
    
    const currencies = [...new Set(walletsToCheck.map(w => w.currency).filter(Boolean))];
    const isValid = currencies.length <= 1;
    const primaryCurrency = currencies.length === 1 ? currencies[0] : (currencies.length === 0 ? 'EUR' : null);
    
    return {
      valid: isValid,
      currencies: currencies,
      primaryCurrency: primaryCurrency,
      message: isValid ? null : `Multiple currencies selected: ${currencies.join(', ')}`
    };
  }, [selectedWallets, wallets]);

  // Date presets
  const setDatePreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch (preset) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const year = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const q = lastQuarter < 0 ? 3 : lastQuarter;
        start = new Date(year, q * 3, 1);
        end = new Date(year, (q + 1) * 3, 0);
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'lastYear':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'last30':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case 'last90':
        start = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const handleWalletToggle = (walletId) => {
    if (walletId === 'all') {
      setSelectedWallets(['all']);
    } else {
      let newSelection = selectedWallets.filter(w => w !== 'all');
      
      if (newSelection.includes(walletId)) {
        newSelection = newSelection.filter(w => w !== walletId);
        if (newSelection.length === 0) {
          setSelectedWallets(['all']);
          return;
        }
      } else {
        newSelection = [...newSelection, walletId];
      }
      
      const selectedWalletsData = wallets.filter(w => newSelection.includes(w.id));
      const currencies = [...new Set(selectedWalletsData.map(w => w.currency))];
      
      if (currencies.length > 1) {
        setError(`⚠️ Warning: Mixed currencies (${currencies.join(', ')}). Export will be disabled.`);
      } else {
        setError(null);
      }
      
      setSelectedWallets(newSelection);
    }
  };

  const canExport = () => {
    return currencyValidation.valid && 
           (includeIncome || includeExpenses || includeHousehold) && 
           !loading;
  };

  const downloadCSV = async () => {
    if (!canExport()) {
      setError('❌ Cannot export: Please select pots with the same currency.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const walletIds = selectedWallets.includes('all') ? 'all' : selectedWallets.join(',');
      
      const response = await api.get('/export/csv', {
        params: {
          start_date: startDate,
          end_date: endDate,
          include_income: includeIncome,
          include_expenses: includeExpenses,
          include_household: includeHousehold,
          wallet_ids: walletIds
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `domusai_export_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('✅ CSV downloaded successfully!');
    } catch (err) {
      console.error('CSV error:', err);
      setError(`❌ ${err.response?.data?.detail || 'Failed to download CSV'}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!canExport()) {
      setError('❌ Cannot export: Please select wallets with the same currency.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const walletIds = selectedWallets.includes('all') ? 'all' : selectedWallets.join(',');
      
      const response = await api.get('/export/pdf', {
        params: {
          start_date: startDate,
          end_date: endDate,
          language: language,
          currency_symbol: currencyValidation.primaryCurrency || 'EUR',
          include_income: includeIncome,
          include_expenses: includeExpenses,
          include_household: includeHousehold,
          wallet_ids: walletIds
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `domusai_report_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('✅ PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF error:', err);
      setError(`❌ ${err.response?.data?.detail || 'Failed to download PDF'}`);
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code;

  const getSelectedWalletsDisplay = () => {
    if (selectedWallets.includes('all')) return 'All Pots';
    return wallets.filter(w => selectedWallets.includes(w.id)).map(w => `${w.name} (${w.currency})`).join(', ');
  };

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-header">
        <h1>📊 Reports & Export</h1>
      </div>

      {/* Disclaimer */}
      <div className="disclaimer-banner">
        <span className="disclaimer-icon">⚠️</span>
        <div className="disclaimer-content">
          <strong>Important Notice</strong>
          <p>Exported reports contain user-inputted data and are NOT official bank statements.</p>
        </div>
      </div>

      <div className="reports-container">
        {/* Date Range */}
        <div className="report-card">
          <h2>📅 Date Range</h2>
          
          <div className="date-presets">
            <button onClick={() => setDatePreset('thisMonth')} className="preset-btn">This Month</button>
            <button onClick={() => setDatePreset('lastMonth')} className="preset-btn">Last Month</button>
            <button onClick={() => setDatePreset('thisQuarter')} className="preset-btn">This Quarter</button>
            <button onClick={() => setDatePreset('lastQuarter')} className="preset-btn">Last Quarter</button>
            <button onClick={() => setDatePreset('thisYear')} className="preset-btn">This Year</button>
            <button onClick={() => setDatePreset('lastYear')} className="preset-btn">Last Year</button>
            <button onClick={() => setDatePreset('last30')} className="preset-btn">Last 30 Days</button>
            <button onClick={() => setDatePreset('last90')} className="preset-btn">Last 90 Days</button>
          </div>
          
          <div className="date-inputs">
            <div className="date-field">
              <label>Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <span className="date-arrow">→</span>
            <div className="date-field">
              <label>End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Data Selection */}
        <div className="report-card">
          <h2>📋 Include Data</h2>
          
          <div className="data-toggles">
            <label className={`data-toggle ${includeIncome ? 'active income' : ''}`}>
              <input type="checkbox" checked={includeIncome} onChange={(e) => setIncludeIncome(e.target.checked)} />
              <span className="toggle-emoji">💰</span>
              <span className="toggle-label">Income</span>
            </label>
            
            <label className={`data-toggle ${includeExpenses ? 'active expense' : ''}`}>
              <input type="checkbox" checked={includeExpenses} onChange={(e) => setIncludeExpenses(e.target.checked)} />
              <span className="toggle-emoji">💸</span>
              <span className="toggle-label">Personal Expenses</span>
            </label>
            
            <label className={`data-toggle ${includeHousehold ? 'active household' : ''}`}>
              <input type="checkbox" checked={includeHousehold} onChange={(e) => setIncludeHousehold(e.target.checked)} />
              <span className="toggle-emoji">🏠</span>
              <span className="toggle-label">Household Expenses</span>
            </label>
          </div>
        </div>

        {/* Wallet Selection */}
        <div className="report-card">
          <h2>🫙 Pots</h2>
          <p className="card-hint">Select pots with the same currency for export</p>
          
          <div className="wallet-chips">
            <label className={`wallet-chip ${selectedWallets.includes('all') ? 'selected' : ''}`}>
              <input type="checkbox" checked={selectedWallets.includes('all')} onChange={() => handleWalletToggle('all')} />
              <span>🌐 All Pots</span>
            </label>
            
            {wallets.map(wallet => (
              <label key={wallet.id} className={`wallet-chip ${selectedWallets.includes(wallet.id) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selectedWallets.includes(wallet.id)} onChange={() => handleWalletToggle(wallet.id)} />
                <span className="chip-currency">{wallet.currency}</span>
                <span>{wallet.name}</span>
              </label>
            ))}
          </div>

          {/* Currency Status */}
          {!currencyValidation.valid && (
            <div className="currency-alert error">
              <strong>❌ Cannot Export - Multiple Currencies</strong>
              <p>Selected wallets use: <strong>{currencyValidation.currencies.join(', ')}</strong></p>
              <p>Please select pots that all use the same currency.</p>
            </div>
          )}
          
          {currencyValidation.valid && currencyValidation.primaryCurrency && (
            <div className="currency-alert success">
              ✅ Export currency: <strong>{getCurrencySymbol(currencyValidation.primaryCurrency)} ({currencyValidation.primaryCurrency})</strong>
            </div>
          )}
        </div>

        {/* Language Selection */}
        <div className="report-card">
          <h2>🌍 PDF Language</h2>
          
          <div className="language-grid">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                className={`language-option ${language === lang.code ? 'selected' : ''}`}
                onClick={() => setLanguage(lang.code)}
              >
                <span className="lang-flag">{lang.flag}</span>
                <span className="lang-name">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Export Section */}
        <div className={`export-card ${!canExport() ? 'disabled' : ''}`}>
          <h2>📤 Export</h2>
          
          {error && <div className="alert error">{error}</div>}
          {success && <div className="alert success">{success}</div>}
          
          <div className="export-buttons">
            <button className="export-btn csv" onClick={downloadCSV} disabled={!canExport()}>
              {loading ? (
                <><span className="spinner"></span> Generating...</>
              ) : (
                <>
                  <span className="btn-emoji">📄</span>
                  <span className="btn-content">
                    <strong>Download CSV</strong>
                    <small>Spreadsheet format</small>
                  </span>
                </>
              )}
            </button>
            
            <button className="export-btn pdf" onClick={downloadPDF} disabled={!canExport()}>
              {loading ? (
                <><span className="spinner"></span> Generating...</>
              ) : (
                <>
                  <span className="btn-emoji">📑</span>
                  <span className="btn-content">
                    <strong>Download PDF</strong>
                    <small>Professional report</small>
                  </span>
                </>
              )}
            </button>
          </div>
          
          {!currencyValidation.valid && (
            <p className="export-hint">⚠️ Export disabled: Select pots with the same currency</p>
          )}
        </div>

        {/* Summary */}
        <div className="report-card summary-card">
          <h2>📝 Export Summary</h2>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">📅 Period</span>
              <span className="summary-value">{startDate} → {endDate}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">📋 Data</span>
              <span className="summary-value">
                {[includeIncome && '💰 Income', includeExpenses && '💸 Expenses', includeHousehold && '🏠 Household'].filter(Boolean).join(', ') || 'None'}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">🫙 Pots</span>
              <span className="summary-value">{getSelectedWalletsDisplay()}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">💱 Currency</span>
              <span className={`summary-value ${!currencyValidation.valid ? 'error' : ''}`}>
                {!currencyValidation.valid 
                  ? `❌ Mixed (${currencyValidation.currencies.join(', ')})` 
                  : `${getCurrencySymbol(currencyValidation.primaryCurrency)} (${currencyValidation.primaryCurrency})`
                }
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">🌍 Language</span>
              <span className="summary-value">{LANGUAGES.find(l => l.code === language)?.flag} {LANGUAGES.find(l => l.code === language)?.name}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
